'use client';

import React, { useEffect, useState, useCallback, useContext } from 'react';
import { UserManager } from '@benzinga/user-manager';
import { SessionContext } from '@benzinga/session-context';

import { FcGoogle } from 'react-icons/fc';
import { CgSpinner } from 'react-icons/cg';
import { HiPlus } from 'react-icons/hi2';
import { MdCheck } from 'react-icons/md';

const PUBLICATION_ID = 'CAowneCICw';

interface LinkSubscriptionResult {
  publisherProvidedId: string | number;
  success: boolean;
}

interface GoogleSubscriptionLinkProps {
  className?: string;
  onLinkedChange?: (isLinked: boolean) => void;
  variant?: 'card' | 'compact';
}

export const GoogleSubscriptionLink: React.FC<GoogleSubscriptionLinkProps> = ({
  className,
  onLinkedChange,
  variant = 'card',
}) => {
  const session = useContext(SessionContext);
  const userManager = session.getManager(UserManager);
  const [isLinking, setIsLinking] = useState(false);
  const [isLinked, setIsLinked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Parent notification rides the state setter so both the initial
  // settings check and a completed link report through one path.
  const onLinkedChangeRef = React.useRef(onLinkedChange);
  onLinkedChangeRef.current = onLinkedChange;
  const applyLinked = useCallback((linked: boolean) => {
    setIsLinked(linked);
    onLinkedChangeRef.current?.(linked);
  }, []);

  useEffect(() => {
    const checkIsLinked = async () => {
      const isGoogleSubLinked = await userManager.getGlobalSettings<boolean>('isGoogleSubLinked');
      applyLinked(isGoogleSubLinked.ok === true);
    };
    checkIsLinked();
  }, [userManager, applyLinked]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/javascript';
    script.async = true;
    script.setAttribute('subscriptions-control', 'manual');
    script.src = 'https://news.google.com/swg/js/v1/swg.js';
    script.onload = () => {
      (window.SWG = window.SWG || []).push((subscriptions: any) => {
        subscriptions.init(PUBLICATION_ID);
      });
      setScriptLoaded(true);
    };
    script.onerror = () => {
      setError('Failed to load Google Subscribe with Google script');
    };

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleLinkSubscription = useCallback(async () => {
    const user = userManager.getUser();
    const publisherProvidedId = user?.id || user?.benzingaUid;

    if (!publisherProvidedId) {
      setError('Unable to retrieve user ID');
      return;
    }

    setIsLinking(true);
    setError(null);

    try {
      if (!window.SWG) {
        throw new Error('Google Subscribe with Google script not loaded');
      }

      const result: LinkSubscriptionResult = await new Promise((resolve, reject) => {
        (window.SWG = window.SWG || []).push(async (subscriptions: any) => {
          try {
            const linkResult = await subscriptions.linkSubscription({
              publisherProvidedId: publisherProvidedId.toString(),
            });
            resolve(linkResult);
          } catch (err) {
            console.error('Error linking subscription:', err);
            reject(err);
          }
        });
      });

      if (result.success) {
        await userManager.setGlobalSetting('isGoogleSubLinked', true).then(() => {
          applyLinked(true);
          setError(null);
        });
      } else {
        setError('Failed to link subscription. Please try again.');
      }
    } catch (err: any) {
      console.error('Error linking subscription:', err);
      setError(err.message || 'An error occurred while linking your subscription');
    } finally {
      setIsLinking(false);
    }
  }, [userManager, applyLinked]);

  if (variant === 'compact') {
    if (isLinked) {
      return (
        <div className={`flex items-center gap-2 ${className || ''}`}>
          <span className="text-base font-semibold text-bzgray-900">Google</span>
          <span className="inline-flex items-center gap-1 text-xs font-bold text-bzgreen-500">
            <MdCheck aria-hidden="true" className="h-4 w-4" />
            Connected
          </span>
        </div>
      );
    }
    return (
      <div className={className}>
        <div className="flex w-full flex-col items-start gap-1.5 lg:flex-row lg:items-center lg:gap-4">
          <button
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-bzblue-400 bg-white px-5 py-2.5 text-sm font-bold text-bzblue-800 shadow-sm transition-colors hover:border-bzblue-700 hover:bg-bzblue-100 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isLinking || !scriptLoaded}
            onClick={handleLinkSubscription}
            type="button"
          >
            <FcGoogle aria-hidden="true" className={isLinking ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Link Your Benzinga Account with Google
            {isLinking ? <span className="sr-only">Linking…</span> : null}
          </button>
          <p className="m-0 min-w-0 flex-1 text-sm leading-relaxed text-bzgray-700">
            Once linked, you&apos;ll be able to read Benzinga articles directly from Google News and other Google
            surfaces.
          </p>
        </div>
        {error && <p className="m-0 mt-1.5 text-xs text-bzred-700">{error}</p>}
      </div>
    );
  }

  if (isLinked) {
    return (
      <div className={`google-subscription-link-card ${className || ''}`}>
        <div className="bg-bzblue-100 border border-bzblue-300 rounded-lg p-6 max-w-lg mx-auto">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <MdCheck className="w-6 h-6 text-bzgreen-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-bzgray-900 mb-1">Subscription Linked</h3>
              <p className="text-sm text-bzgray-600">
                Your Benzinga subscription has been successfully linked with Google. You can now access your content
                through Google News.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`google-subscription-link-card ${className || ''}`}>
      <div className="bg-white border border-bzblue-300 rounded-lg p-6 max-w-lg mx-auto shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-bzblue-100 rounded-full flex items-center justify-center">
              <HiPlus className="w-6 h-6 text-bzblue-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-bzgray-900 mb-2">Link Your Benzinga Account with Google</h3>
            <p className="text-sm text-bzgray-600 mb-4">
              Connect your Benzinga account to Google News to access your Benzinga content seamlessly. Once linked,
              you'll be able to read Benzinga articles directly from Google News and other Google surfaces.
            </p>
            {error && (
              <div className="mb-4 p-3 bg-bzred-50 border border-bzred-200 rounded text-sm text-bzred-700">{error}</div>
            )}
            <button
              onClick={handleLinkSubscription}
              disabled={isLinking || !scriptLoaded}
              className="w-full bg-bzblue-700 hover:bg-bzblue-800 text-white font-semibold py-2.5 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLinking ? (
                <>
                  <CgSpinner className="animate-spin h-4 w-4 text-white" />
                  Linking...
                </>
              ) : (
                <>
                  <FcGoogle />
                  Continue with Google
                </>
              )}
            </button>
            <p className="text-xs text-bzgray-500 mt-3 text-center">
              By continuing, you agree to link your Benzinga subscription with your Google account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
